import { Injectable, EventEmitter } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { IHttpConnectionOptions } from '@microsoft/signalr';
import { AuthenticationService } from 'projects/shared/src/services/authentication.service';
import { environment } from 'public/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SignalRServiceService {
  signalReceived = new EventEmitter<any>();

  // Remove the exclamation mark (!) and allow it to be undefined safely initially,
  // or initialize it immediately.
  private hubConnection!: signalR.HubConnection;

  constructor(private authService: AuthenticationService) {
    // Optional: You can safely build the connection configuration here
    // so this.hubConnection is instantly defined.
    this.createConnectionInstance();
  }

  private createConnectionInstance() {
    const options: IHttpConnectionOptions = {
      transport: signalR.HttpTransportType.LongPolling,
      accessTokenFactory: () => this.authService.currentUserValue?.Token ?? ''
    };

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalR, options)
      .build();
  }

  // Change this to only focus on STARTING the connection
  public buildConnection = () => {
    // If somehow it wasn't instantiated, make it now
    if (!this.hubConnection) {
      this.createConnectionInstance();
    }

    this.hubConnection
      .start()
      .then(() => console.log('Connection started ...'))
      .catch((err: any) => console.log('Error while starting connection ... ' + err));
  }

  // A helper method to register listeners safely
  private registerListener(eventName: string, callback: (...args: any[]) => void) {
    if (this.hubConnection) {
      this.hubConnection.on(eventName, callback);
    } else {
      console.warn(`SignalR: Tried to register listener for '${eventName}' but hubConnection is not ready yet.`);
    }
  }

  /* ==========================================
     UPDATED LISTENERS USING THE SAFE REGISTRATION
     ========================================== */

  handleChangeNotificationCountEvent(context: any, handleFunc: any) {
    this.registerListener('changeNotificationCount', (messageCount: any) => {
      handleFunc(context, messageCount);
    });
  }

  handleNewMessageHubEvent(context: any, handleFunc: any) {
    this.registerListener('changeMessageCount', (messageCount: any) => {
      handleFunc(context, messageCount);
    });
  }

  handleLoadMessageListEvent(context: any, handleFunc: any) {
    this.registerListener('loadMessageList', () => {
      handleFunc(context);
    });
  }

  handleLoadNewGroupVoteEvent(context: any, handleFunc: any) {
    this.registerListener('newgroupVote', (mileStoneId: any) => {
      handleFunc(context, mileStoneId);
    });
  }

  handleLoadMessageCommentsEvent(context: any, handleFunc: any) {
    this.registerListener('loadMessageComments', (messageId: any) => {
      handleFunc(context, messageId);
    });
  }

  handleReadUnReadMessageEvent(context: any, handleFunc: any) {
    this.registerListener('ReadUnReadMessage', (messageId: any) => {
      handleFunc(context, messageId);
    });
  }

  handleChangeCommentNumberEvent(context: any, handleFunc: any) {
    this.registerListener('changeCommentNumber', (messageId: any, commentsNumber: any) => {
      handleFunc(context, messageId, commentsNumber);
    });
  }

  handleChangeReplayNumberEvent(context: any, handleFunc: any) {
    this.registerListener('changeReplayNumber', (messageId: any, commentsNumber: any) => {
      handleFunc(context, messageId, commentsNumber);
    });
  }

  handleLoadCommentReplaiesEvent(context: any, handleFunc: any) {
    this.registerListener('loadCommentReplaies', (commentId: any, parentId: any) => {
      if (parentId === -1) {
        parentId = null;
      }
      handleFunc(context, commentId, parentId);
    });
  }

  handleChangeVoteNumberEvent(context: any, handleFunc: any) {
    this.registerListener('changeVoteNumber', (messageId: any, upVoteNumber: any, downVoteNumber: any) => {
      handleFunc(context, messageId, upVoteNumber, downVoteNumber);
    });
  }

  handleEntityNewMemberEvent(context: any, handleFunc: any) {
    this.registerListener('entityNewMember', (entityId: any) => {
      handleFunc(context, entityId);
    });
  }

  handleGroupNewEntityEvent(context: any, handleFunc: any) {
    this.registerListener('groupNewEntity', (groupId: any) => {
      handleFunc(context, groupId);
    });
  }

  handleGroupNewUserEvent(context: any, handleFunc: any) {
    this.registerListener('groupNewUser', (groupId: any) => {
      handleFunc(context, groupId);
    });
  }

  handleMainTaskListChangeEvent(context: any, handleFunc: any) {
    this.registerListener('mainTaskListChange', (groupId: any) => {
      handleFunc(context, groupId);
    });
  }

  handleMainTaskDetailsChangeEvent(context: any, handleFunc: any) {
    this.registerListener('mainTaskDetailsChange', (taskId: any, taskType: any) => {
      handleFunc(context, taskId, taskType);
    });
  }

  handleSubTaskListChangeEvent(context: any, handleFunc: any) {
    this.registerListener('mainTaskDetailsChange', (entityId) => {
      handleFunc(context, entityId);
    });
  }

  handleRefreshMeetingListEvent(context: any, handleFunc: any) {
    this.registerListener('refreshMeetingList', () => {
      handleFunc(context);
    });
  }

  handleGoLiveEvent(context: any, handleFunc: any) {
    this.registerListener('goLive', (meetingId) => {
      handleFunc(context, meetingId);
    });
  }

  handleMeetingChangedEvent(context: any, handleFunc: any) {
    this.registerListener('meetingChanged', (meetingId) => {
      handleFunc(context, meetingId);
    });
  }

  handleNewRecommendationEvent(context: any, handleFunc: any) {
    this.registerListener('newRecommendation', (meetingId) => {
      handleFunc(context, meetingId);
    });
  }

  handleStartVotingEvent(context: any, handleFunc: any) {
    this.registerListener('startVoting', (agendaItemId, votingTime) => {
      handleFunc(context, agendaItemId, votingTime);
    });
  }

  handleEndVotingEvent(context: any, handleFunc: any) {
    this.registerListener('endVoting', (agendaItemId, votingTime) => {
      handleFunc(context, agendaItemId, votingTime);
    });
  }

  handleNewVotingEvent(context: any, handleFunc: any) {
    this.registerListener('newVoting', (agendaItemId, canEndVote) => {
      handleFunc(context, agendaItemId, canEndVote);
    });
  }

  handleNewDelegationEvent(context: any, handleFunc: any) {
    this.registerListener('newDelegation', (meetingId) => {
      handleFunc(context, meetingId);
    });
  }

  handleUpdateInviteesListEvent(context: any, handleFunc: any) {
    this.registerListener('updateInviteesList', (meetingId) => {
      handleFunc(context, meetingId);
    });
  }

  handleRefreshDashBoardEvent(context: any, handleFunc: any) {
    this.registerListener('RefreshDashBoard', (target) => {
      handleFunc(context, target);
    });
  }

  handleNewChatMessageEvent(context: any, handleFunc: any) {
    this.registerListener('newChatMessage', (loggedUserId, messageContent) => {
      handleFunc(context, loggedUserId, messageContent);
    });
  }

  handleRefreshUserPermissionsEvent(context: any, handleFunc: any) {
    this.registerListener('refreshUserPermissions', () => {
      handleFunc(context);
    });
  }

  handleLogOutInActiveUserEvent(context: any, handleFunc: any) {
    this.registerListener('LogOutInActiveUser', (logoutforedit) => {
      handleFunc(context, logoutforedit);
    });
  }

  handleTreatmentPlaneVotingEvent(context: any, handleFunc: any) {
    this.registerListener('TreatmentPlaneVoting', (canEndVote, agendaItemId, eventtype) => {
      handleFunc(context, canEndVote, agendaItemId, eventtype);
    });
  }

  handleTreatmentPlaneEvent(context: any, handleFunc: any) {
    this.registerListener('TreatmentPlane', (agendaItemId) => {
      handleFunc(context, agendaItemId);
    });
  }

  handleRecommendationVotingEvent(context: any, handleFunc: any) {
    this.registerListener('RecommendationVoting', (canEndVote, agendaItemId, eventtype) => {
      handleFunc(context, canEndVote, agendaItemId, eventtype);
    });
  }

  handleRecommendationPlaneEvent(context: any, handleFunc: any) {
    this.registerListener('RecommendationPlane', (agendaItemId) => {
      handleFunc(context, agendaItemId);
    });
  }

  handleSubmitDocumentSignature(context: any, handleFunc: any) {
    this.registerListener('SubmitDocumentSignature', (requestId) => {
      handleFunc(context, requestId);
    });
  }
}
